import { useEffect } from 'react';
import { BellOff, BellRing, Share } from 'lucide-react';
import { useT } from '../../lib/i18n';
import { Toggle } from '../../ui';
import { disablePush, enablePush, refreshPushState, usePush } from '../../lib/push';
import { toast } from '../../lib/session';

// States where notifications can't be switched on here: an icon and why.
const INFO: Partial<Record<ReturnType<typeof usePush.getState>['state'], { icon: typeof BellOff; text: 'push.appUpdate' | 'push.install' | 'push.blocked' }>> = {
  'needs-app-update': { icon: BellOff, text: 'push.appUpdate' },
  'needs-install': { icon: Share, text: 'push.install' },
  blocked: { icon: BellOff, text: 'push.blocked' },
};

/** "Notify me on this device" — room invites as real notifications, even with the app closed. */
export function PushToggle({ compact }: { compact?: boolean }) {
  const t = useT();
  const { state, busy } = usePush();
  useEffect(() => {
    refreshPushState();
  }, []);
  if (state === 'unsupported') return compact ? null : <p className="muted push-note">{t('push.unsupported')}</p>;
  const info = INFO[state];
  if (info) {
    const Icon = info.icon;
    return (
      <div className={`push-row${compact ? ' compact' : ''}`}>
        <Icon size={16} />
        <span>{t(info.text)}</span>
      </div>
    );
  }
  const on = state === 'on';
  const toggle = async () => {
    try {
      const s = on ? await disablePush() : await enablePush();
      if (!on && s === 'on') toast(t('push.enabled'), 'accent');
      if (s === 'blocked') toast(t('push.blocked'), 'red');
    } catch (e) {
      toast((e as Error).message, 'red');
    }
  };
  return (
    <div className={`push-row${compact ? ' compact' : ''}${on ? ' on' : ''}`}>
      {on ? <BellRing size={16} /> : <BellOff size={16} />}
      <span>{on ? t('push.on') : t('push.off')}</span>
      <button className={`btn btn-sm ${on ? 'btn-ghost' : 'btn-black'}`} disabled={busy} onClick={toggle}>
        {on ? t('push.turnOff') : t('push.turnOn')}
      </button>
    </div>
  );
}

/**
 * Settings → App: the same switch as one compact row: an icon, what it does, and a toggle.
 * (A full-width tinted banner with a button was far bigger than a single on/off deserves.)
 */
export function PushCard() {
  const t = useT();
  const { state, busy } = usePush();
  useEffect(() => {
    refreshPushState();
  }, []);
  const on = state === 'on';
  const info = INFO[state];
  const canToggle = state !== 'unsupported' && !info;
  const toggle = async () => {
    try {
      const s = on ? await disablePush() : await enablePush();
      if (!on && s === 'on') toast(t('push.enabled'), 'accent');
      if (s === 'blocked') toast(t('push.blocked'), 'red');
    } catch (e) {
      toast((e as Error).message, 'red');
    }
  };
  const Icon = on ? BellRing : (info?.icon ?? BellOff);
  let sub: string = t('push.lead');
  if (state === 'unsupported') sub = t('push.unsupported');
  else if (info) sub = t(info.text);
  else if (on) sub = t('push.onShort');
  return (
    <section className={`card push-card${on ? ' on' : ''}`} data-anim>
      <span className="push-card-icon" aria-hidden>
        <Icon size={17} />
      </span>
      <div className="push-card-text">
        <h2>{t('push.title')}</h2>
        <p>{sub}</p>
      </div>
      {canToggle && (
        <span className={busy ? 'push-card-busy' : undefined}>
          <Toggle on={on} onChange={() => void (busy ? undefined : toggle())} label={t('push.title')} />
        </span>
      )}
    </section>
  );
}
